import React from "react";
import SummaryCard from "./SummaryCard";
import { AbandonmentInput, SoilVerificationData, SoilProfile, SoilProfileHorizon } from "../AbandonmentForm.types";
import { useData } from "../../../hooks";
import { getComplianceNameById } from "./summaryHelpers";
import { ComplianceData } from "../DrainFieldInstallationInspectionForm.types";

// Constants.Out_of_Compliance = 3 (hardcoded to avoid import issues)
const OUT_OF_COMPLIANCE = 3;

type Props = {
    data?: AbandonmentInput;
    applicationInspectionId?: number;
    complianceData?: ComplianceData[];
};

const AbandonmentReadOnly: React.FC<Props> = ({ data, applicationInspectionId, complianceData }) => {
    // Load soil verification data separately (same as form does)
    // Note: Hook must be called before any early returns to comply with Rules of Hooks
    const soilVerificationData = useData<SoilVerificationData>(`SoilVerification/abandonment/${applicationInspectionId}`) || null;
    
    if (!data) return null;
    
    // Use soil profiles from separate API call if available, otherwise fall back to data.soilProfiles
    const soilProfiles = soilVerificationData?.soilProfiles || data?.soilProfiles || [];
    
    return (
        <SummaryCard
            title={
                <>
                    <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8V7a2 2 0 0 0-2-2h-3V3a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v2H5a2 2 0 0 0-2 2v1"/><path d="M3 8h18"/><path d="M19 8l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8"/><line x1="10" y1="12" x2="10" y2="17"/><line x1="14" y1="12" x2="14" y2="17"/></svg>
                    Abandonment / Soils Verification
                </>
            }
        >
            <div className="row mb-3">
                <div className="col col-md-4">
                    <label>{getComplianceNameById(complianceData, data?.tankPumpedComplianceId)}</label>
                </div>
                <div className="col col-md-4">
                    <label> Tank Pumped</label>
                    {data?.tankPumpedDate && (
                        <span className="print-value">{` (${new Date(data?.tankPumpedDate as any).toLocaleDateString()})`}</span>
                    )}
                </div>
            </div>
            {data?.tankPumpedComment && (
                <div className="row mb-3">
                    <label>{data?.tankPumpedComment}</label>
                </div>
            )}

            <div className="row mb-3">
                <div className="col col-md-4">
                    <label>{getComplianceNameById(complianceData, data?.tankCrushedFilledComplianceId)}</label>
                </div>
                <div className="col col-md-4">
                    <label> Tank Crushed and Filled</label>
                    {data?.tankCrushedFilledDate && (
                        <span className="print-value">{` (${new Date(data?.tankCrushedFilledDate as any).toLocaleDateString()})`}</span>
                    )}
                </div>
            </div>
            {data?.tankCrushedFilledComment && (
                <div className="row mb-3">
                    <label>{data?.tankCrushedFilledComment}</label>
                </div>
            )}

            <div className="row mb-3">
                <div className="col col-md-4">
                    <label>{getComplianceNameById(complianceData, data?.soilVerificationComplianceId)}</label>
                </div>
                <div className="col col-md-4">
                    <label> Soil Verification</label>
                </div>
            </div>
            {data?.soilVerificationComment && (
                <div className="row mb-3">
                    <label>{data?.soilVerificationComment}</label>
                </div>
            )}

            {/* Soil Profile Information - Show when soil verification is out of compliance (same business rule as form) */}
            {Number(data?.soilVerificationComplianceId) === OUT_OF_COMPLIANCE && (
                <div className="row mb-3">
                    <div className="col col-md-12">
                        <h6 className="mb-3 fw-bold text-primary">
                            <svg className="me-2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7L12 12L22 7L12 2Z"/>
                                <path d="M2 17L12 22L22 17"/>
                                <path d="M2 12L12 17L22 12"/>
                            </svg>
                            Soil Profile Data
                        </h6>

                        {/* New Soil Profile Depth Fields */}
                        <div className="row mb-3">
                            <div className="col col-md-6">
                                <div className="mb-2">
                                    <strong>Soil Profile 1 Depth:</strong>
                                    <span className="ms-2">
                                        {data?.soilProfile1Depth !== undefined && data?.soilProfile1Depth !== null 
                                            ? `${data.soilProfile1Depth}"`
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <strong>Above or Below Benchmark:</strong>
                                    <span className="ms-2">
                                        {data?.soilProfile1DepthBenchmark === 1 
                                            ? "Above"
                                            : data?.soilProfile1DepthBenchmark === 2 
                                            ? "Below"
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                            </div>
                            <div className="col col-md-6">
                                <div className="mb-2">
                                    <strong>Soil Profile 2 Depth:</strong>
                                    <span className="ms-2">
                                        {data?.soilProfile2Depth !== undefined && data?.soilProfile2Depth !== null 
                                            ? `${data.soilProfile2Depth}"`
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <strong>Above or Below Benchmark:</strong>
                                    <span className="ms-2">
                                        {data?.soilProfile2DepthBenchmark === 1 
                                            ? "Above"
                                            : data?.soilProfile2DepthBenchmark === 2 
                                            ? "Below"
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Water Table Fields */}
                        <div className="row mb-3">
                            <div className="col col-md-6">
                                <div className="mb-2">
                                    <strong>Observed Water Table:</strong>
                                    <span className="ms-2">
                                        {data?.observedWaterTable !== undefined && data?.observedWaterTable !== null 
                                            ? `${data.observedWaterTable}"`
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <strong>Above or Below Benchmark:</strong>
                                    <span className="ms-2">
                                        {data?.observedWaterTableBenchmark === 1 
                                            ? "Above"
                                            : data?.observedWaterTableBenchmark === 2 
                                            ? "Below"
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                            </div>
                            <div className="col col-md-6">
                                <div className="mb-2">
                                    <strong>Estimated Wet Season Water Table Elevation:</strong>
                                    <span className="ms-2">
                                        {data?.estimatedWetSeasonWaterTableElevation !== undefined && data?.estimatedWetSeasonWaterTableElevation !== null 
                                            ? `${data.estimatedWetSeasonWaterTableElevation}"`
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                                <div className="mb-2">
                                    <strong>Above or Below Benchmark:</strong>
                                    <span className="ms-2">
                                        {data?.estimatedWetSeasonWaterTableElevationBenchmark === 1 
                                            ? "Above"
                                            : data?.estimatedWetSeasonWaterTableElevationBenchmark === 2 
                                            ? "Below"
                                            : "Not specified"
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        <hr className="my-3" />
                        
                        {soilProfiles && soilProfiles.length > 0 ? soilProfiles.map((profile: SoilProfile, index: number) => (
                            <div key={index} className="mb-4 p-3 border rounded bg-light">
                                <h6 className="text-secondary mb-2">
                                    {index === 0 ? "Primary Soil Profile" : "Secondary Soil Profile"}
                                </h6>
                                
                                {/* USDA Soil Series */}
                                <div className="row mb-2">
                                    <div className="col col-md-12">
                                        <strong>USDA Soil Series:</strong> 
                                        <span className="ms-2">{profile.usdaSoilSeries || "Not specified"}</span>
                                    </div>
                                </div>

                                {/* Soil Horizons */}
                                {profile.horizons && profile.horizons.length > 0 && (
                                    <div className="mt-3">
                                        <strong>Soil Horizons:</strong>
                                        <div className="table-responsive mt-2">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-secondary">
                                                    <tr>
                                                        <th>Horizon</th>
                                                        <th>Depth (inches)</th>
                                                        <th>Munsell Color</th>
                                                        <th>Soil Texture</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {profile.horizons.map((horizon: SoilProfileHorizon, horizonIndex: number) => (
                                                        <tr key={horizonIndex}>
                                                            <td>{horizon.horizonNumber || horizonIndex + 1}</td>
                                                            <td>
                                                                {horizon.depthFrom !== undefined && horizon.depthTo !== undefined 
                                                                    ? `${horizon.depthFrom}" - ${horizon.depthTo}"`
                                                                    : "Not specified"
                                                                }
                                                            </td>
                                                            <td>
                                                                {horizon.munsellColor || "Not specified"}
                                                                {horizon.colorDescription && (
                                                                    <small className="text-muted d-block">
                                                                        ({horizon.colorDescription})
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td>{horizon.soilTexture || "Not specified"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                                
                                {(!profile.horizons || profile.horizons.length === 0) && (
                                    <div className="text-muted mt-2">
                                        <em>No soil horizon data available for this profile</em>
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="text-muted">
                                <em>Soil profile documentation required but not yet completed</em>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </SummaryCard>
    );
};

export default AbandonmentReadOnly;


